import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { 
  defaultValuesInternetBanking, defaultValuesLogin, defaultValuesRecoverPassword,
  paramsAdd, profile, schemaClientEntity, schemaEmail, schemaGetStarted,
  schemaInternetBankingUser, schemaLogin, schemaRecoverPassword, browserNames,
  operativeSystemNames, defaultValuesClientEntity, defaultValuesEmail, defaultValuesGetStarted
} from '../constants/form';
import { routes, siteMap } from '../constants/routes';
import { fileStatus } from '../constants/files';
import { envSetting } from '../constants/envSetting';
import { appsOrigin, logoAdsAppOrigin, logoBgAppOrigin } from '../constants/appOrigin';
import { 
  changePassword, getDataSummary, getUserApplications, 
  getTimeRefreshProducts, loginUser, recoverPasswordEmail,
  registerClientEntity, setLogout, signUpUser, verifyUser,
  getProductRequest, createInternetBankingUser
 } from '../redux/actions/user';
import { setLoader, setPassword } from '../redux/actions/shared';
import { setMessage } from '../redux/actions/message';
import { getBase, getAproxUserGeolocation } from '../redux/actions/base';
import { getRejectedFiles, getUserImages, sendCheckUserTier } from '../redux/actions/files';
import { getStaticData } from '../redux/actions/staticData';
import { updatePersonData, getPersonData, setAvatarFromPersonData } from '../redux/actions/personData';
import {
  USER_APP_ORIGIN,
  USER_INFO_PRODUCT_REQUEST,
  USER_LOGIN,
  USER_SELECTED_PRODUCT,
  USER_APP_ORIGIN_DATA,
  USER_SET_IS_EXPECTING_NEW_DATA_PRODUCT_REQUEST
} from '../redux/types/user';
import {
  actualProductRequest,
  checkMinutesPassed,
  fieldUniqueName,
  isEmpty,
  isUndefinedNull,
  notUndefinedNull,
  setMask,
  trimLowerCase
} from '../helpers/shared';
import { useDetect } from './useDetect';
import { sendGetEndpoint } from '../services/baseApi';
import { endpoints } from '../constants/endpoints';
import { CiUser, CiShoppingCart, CiViewList } from "react-icons/ci";
import { useSwitchDesign } from './useSwitchDesign';


/**
 * Hook to values init
 * @param {Boolean} allowed
 * @returns
 */
export const useInitUser = (allowed, setIsOptInModalOpen = null) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { appOrigin, isAuthenticated, applications } = useSelector(state => state.user);

  const { actualLanguage, loading, locationStatus } = useSelector(
    state => state.base);
  const [isSlideModalOpen, setIsSlideModalOpen] = useState(false);
  const [values, setValues] = useState([]);

  useEffect(() => {
    if (location.pathname.includes('logout')) {
      dispatch(setLogout());
      // Send the user back to /login
      setTimeout(() => { 
        navigate(routes.login('credinomina'))
      }, 500);
    } else {
      if (isAuthenticated) navigate(siteMap.homepage.path);
    }
  }, [isAuthenticated, navigate, location]);

  // Automatically send us to /logout path to reset redux buggy state
  useEffect(() => {
    if (!location.pathname.includes('logout') && loading){
      navigate(siteMap.logout.path)
    }
  }, [])

  useEffect(() => {
    if (!applications) {
      dispatch(getUserApplications());
    } else {
      let product = applications.filter(
        product => product?.application?.unique_name === appOrigin);
      if (product.length === 0 ) product = [{
        image_ads: logoAdsAppOrigin.default, image_main: logoBgAppOrigin.default}];
      const [selectProduct] = product;
      dispatch({ type: USER_SELECTED_PRODUCT, payload: selectProduct });
    };
  }, [applications, appOrigin]);

  const { browserInfo, OS } = useDetect()
  // Should detect the Chrome for iOS (CriOS) buggy combination
  const isBrowserAllowed = !( (trimLowerCase(browserInfo?.name) === trimLowerCase(browserNames.crios) || trimLowerCase(browserInfo?.name) === trimLowerCase(browserNames.unknown)) && (trimLowerCase(OS) === trimLowerCase(operativeSystemNames.iOS)) )
  const onSubmit = values => {
    if(isBrowserAllowed){
      if (allowed) {
        dispatch(setLoader(false));
        setIsSlideModalOpen(true);
        setValues(values);
      } else {
        dispatch(setMessage(t("common.countryNotAllowed"), 'error'));
      }
    } else{
      dispatch(setLoader(true))
      setTimeout(() => {
        dispatch(setMessage(t('common.browserNotAllowed'), 'error'))
        dispatch(setLoader(false))
      }, 1000);
    }
  };

  // * Register function to be used in the HomepageStarterEmail component
  // * It will need to implement the Opt in logic.
  const onNewRegister = async values => {
    if ((allowed && isBrowserAllowed)) {
      dispatch(setLoader(false));
      // setIsSlideModalOpen(true); // ? SlideCaptcha modal
      setIsOptInModalOpen(true);    // ? Optin modal
      setValues(values);
    } else {
      dispatch(setLoader(true))
      setTimeout(() => {
        if (!isBrowserAllowed) {
          dispatch(setMessage(t('common.browserNotAllowed'), 'error'))
        } else {
          dispatch(setMessage(t("common.countryNotAllowed"), 'error'))
        }
        dispatch(setLoader(false))
      }, 1000);
    }
  }

  return {
    actualLanguage,
    appOrigin,
    isSlideModalOpen,
    loading,
    onSubmit,
    setIsSlideModalOpen,
    values,
    locationStatus: locationStatus?.active,
    onNewRegister
  };
};

/**
 * Hook to get the values to Login
 * @returns
 */
export const useLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { allowed, geolocation, deviceInfo, loading } = useSelector(
    state => state.base);
    const { applications } = useSelector(state => state.user);
  const methods = useForm({
    resolver: yupResolver(schemaLogin),
    defaultValues: defaultValuesLogin
  });
  const { register, formState: { errors }, handleSubmit, reset, setValue, getValues } = methods

  const onLogin = async (values) => {
    try {
      dispatch(setLoader(true));
      reset();

      if (notUndefinedNull(deviceInfo)) {
        const data = {
          ...values,
          ip_info: {
            ...deviceInfo,
            ...geolocation
          }
        };
        const response = await dispatch(loginUser(data));
        if (!response.error && response?.success) {
          const {
            token,
            info,
            local_user_info: localUserInfo,
            last_product_request_id: lastProductRequestId
          } = response.success;
          const appOriginData = await dispatch(getPersonData(info.user_id, lastProductRequestId));
          await dispatch(setAvatarFromPersonData(appOriginData));
          if (appOriginData) {
            if (await dispatch(getStaticData())) {
              dispatch({
                type: USER_LOGIN,
                payload: {
                  userIdBifrost: info.user_id,
                  username: info.username,
                  token: token,
                  lastProductRequestId: lastProductRequestId
                }
              });
              await dispatch(getUserImages(info.user_id));
              await dispatch(getBase());
              if (await dispatch(sendCheckUserTier())) {
                dispatch(getRejectedFiles());
                dispatch({ type: USER_APP_ORIGIN, payload: appOriginData });
                dispatch(getProductRequest(localUserInfo.id));
                dispatch(setLoader(false));
              } else {
                dispatch(setLogout());
                dispatch(setLoader(false));
                dispatch(setMessage(t("message.onLogin"), 'error'));
              }
              let productsAndServices = [];
              if (!applications) {
                productsAndServices = await dispatch(getUserApplications());
              } else {
                productsAndServices = applications;
              }
              let product = productsAndServices.filter(
                product => product?.application?.unique_name === appOriginData);
              if (product.length === 0) product = [{image_main: logoBgAppOrigin.default }];
              const [productRequest] = product;
              dispatch({ type: USER_INFO_PRODUCT_REQUEST, payload: productRequest });
            }
          }
        } else {
          dispatch(setLoader(false));
        }
      } else {
        dispatch(setLoader(false));
        dispatch(setMessage(t("message.onLogin"), 'error'));
      }
    } catch (error) {
      console.error('onLogin in useLogin : ', error);
      if (!loading) dispatch(setLoader(false));
      dispatch(setMessage(t("message.onLogin"), 'error'));
    }
  };

  const onGetStarted = () => {
    if (allowed) {
      if (notUndefinedNull(geolocation)) {
        navigate(routes.getStartedEmail(appsOrigin.default));
      } else {
        dispatch(setMessage(t("common.requestActivateGeolocation"), 'error'));
      }
    } else {
      dispatch(setMessage(t("common.countryNotAllowed"), 'error'));
    }
  };

  const activateLocation = async () => {
    try {
      const response = await dispatch(getAproxUserGeolocation())
      if (response) dispatch(setMessage(t("message.geolocationSuccess"), 'success'));
    } catch (error) {
      dispatch(setMessage(t("message.geolocationError"), 'error'));
      console.error(error)
    }
  };
  
  useEffect(() => {
    if (isUndefinedNull(geolocation)) {
      activateLocation()
    }
  }, [])

  return {
    errors, handleSubmit, onGetStarted, onLogin, register, activateLocation, reset, methods, setValue, getValues
  };
};

/**
 * Hook to get the values to GetStartedEmail
 * @returns
 */
export const useGetStartedEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loading } = useSelector(state => state.base);
  const { appOrigin, applications, timeRefresh, appOriginData } = useSelector(state => state.user);
  const methods = useForm({
    resolver: yupResolver(schemaEmail),
    defaultValues: defaultValuesEmail
  });
  const { register, formState: { errors }, handleSubmit, reset, getValues } = methods

  const timeRefreshRef = useRef(timeRefresh);
  timeRefreshRef.current = timeRefresh;
  const { isNewDesign } = useSwitchDesign()

  const mailValues = getValues()
  useEffect(() => {
    if (appOrigin !== appsOrigin.default) dispatch({ type: USER_APP_ORIGIN, payload: appsOrigin.default });

    const refreshIfNeeded = () => {
      const currentTimeRefresh = timeRefreshRef.current;
      if (notUndefinedNull(currentTimeRefresh)) {
        if (checkMinutesPassed(currentTimeRefresh, envSetting.minutesRefreshApplication)) {
          dispatch(getTimeRefreshProducts());
          dispatch(getUserApplications());
        }
      } else {
        dispatch(getTimeRefreshProducts());
        dispatch(getUserApplications());
      }
    };

    refreshIfNeeded();

    // Refresh every 10 seconds
    // ? This fix was needed because the useEffect was only being called when
    // ? Accesing the component for the first time.
    const intervalId = setInterval(refreshIfNeeded, 10 * 1000);

    return () => clearInterval(intervalId);
  }, [dispatch]);

  // ? Storing User app origin data in redux.
  useEffect(() => {
    if (appOrigin === appsOrigin.default) {
      dispatch({ type: USER_APP_ORIGIN_DATA, payload: {} });
    } 
  }, [appOrigin])

    // ? Logic for dynamic background
  const [productBackground, setProductBackground] = useState(null)
  useEffect(() => {

    /* if (appOriginData.background_img_url)  */
    if (appOrigin === appsOrigin.default || !appOrigin) {
      setProductBackground(null)
    } else {
      setProductBackground(appOriginData?.background_img_url ?? null)
    }

  }, [appOriginData?.background_img_url, appOrigin])



  // ? State to use after to control the 'signUp' status
  const [isSignUpMailSent, setIsSignUpMailSent] = useState(false)

  const switchMailSent = () => {
    onSignUp(getValues())
  }

  const initialTime = 30
  const [tick, setTick] = useState(initialTime)
  const [isSending, setIsSending] = useState(false)

  const onSignUp = async values => {
    try {
      setIsSending(() => true)
      dispatch(setLoader(true));
      // reset();
      // debugger
      values['application_origin'] = appOrigin;
      const signUpResponse = await dispatch(signUpUser(values));
      if (signUpResponse?.code === 201) {
        setIsSignUpMailSent(true)
        setTick(initialTime)
      }
      dispatch(setLoader(false));
    } catch (error) {
      console.error('onSignUp in useGetStartedEmail: ', error);
      if (!loading) dispatch(setLoader(false));
      dispatch(setMessage(t("message.signUpUser"), 'error'));
    } finally {
      setIsSending(() => false)
    }
  };

  const onProduct = product => {
    if (isNewDesign) {
      dispatch({
        type: USER_APP_ORIGIN_DATA,
        payload: product
      })
    }
    navigate(routes.getStartedEmail(product.application.unique_name))
  
  };

  // ? When coming for the 'create_product' path, requiring a new productRequest
  useNewProductRequestFromQueryParam()

  return { 
    applications, errors, handleSubmit, onProduct, onSignUp, register, methods,
    switchMailSent, isSignUpMailSent, mailValues, productBackground,
    tick, setTick, isSending
  };
};

export const useNewProductRequestFromQueryParam = () => {
  const dispatch = useDispatch()
  // ? When coming for the 'create_product' path, requiring a new productRequest
  const [searchParams, setSearchParams] = useSearchParams()
  const isCreate = searchParams.get('create_product'); 

  // ? Mark Redux as flagged for productRequest creation
  useEffect(() => {
    if (isCreate) {
      dispatch({
        type: USER_SET_IS_EXPECTING_NEW_DATA_PRODUCT_REQUEST,
        payload: true
      })
    } else {
      dispatch({
        type: USER_SET_IS_EXPECTING_NEW_DATA_PRODUCT_REQUEST,
        payload: null
      })
    }
  }, [searchParams])

  return {}
}


/**
 * Hook to get the values to useGetStarted
 * @returns
 */
export const useGetStarted = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loading } = useSelector(state => state.base);
  const { appOrigin } = useSelector(state => state.user);
  const appOriginParam = params.hash.split('&').pop().replace('app_origin=', '');
  const methods = useForm({
    resolver: yupResolver(schemaGetStarted),
    defaultValues: defaultValuesGetStarted
  });

  const { register, formState: { errors }, handleSubmit, getValues, setValue, reset } = methods

  useEffect(() => {
    if (appOrigin !== appOriginParam) dispatch(
      { type: USER_APP_ORIGIN, payload: appOriginParam });
  }, [appOriginParam]);

  const onSignUp = async values => {
    try {
      dispatch(setLoader(true));
      values['hash'] = params?.hash.split('&').shift();
      const { termservice, ...restValues } = values || {};
      const response = await dispatch(verifyUser(restValues));
      dispatch(setLoader(false));
      const formValues = getValues();
      Object.keys(formValues).forEach(key => { 
        setValue(key, '');
      })
      if (!response) navigate(routes.login(appOrigin));
    } catch (error) {
      console.error('onSignUp in useGetStarted: ', error);
      if (!loading) dispatch(setLoader(false));
      dispatch(setMessage(t("message.verifyUser"), 'error'));
    }
  };

  return { errors, handleSubmit, onSignUp, register, methods };
};

/**
 * Hook to get the values to useGetStarted
 * @returns
 */
export const useInternetBanking = (allowed) => {
  const { register, formState: { errors }, handleSubmit, reset } = useForm({
    resolver: yupResolver(schemaInternetBankingUser),
    defaultValues: defaultValuesInternetBanking
  });
  const [isSlideModalOpen, setIsSlideModalOpen] = useState(false);
  const [values, setValues] = useState([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading } = useSelector(state => state.base);
  const { userIdBifrost, lastProductRequestId, selectedProduct } = useSelector(state => state?.user);
  const ebankingCreationPending = useSelector(
    state => state?.personData?.data?.product_request?.user?.ebanking_user_creation_pending);
  const userId = useSelector(state => state?.personData?.data?.client_person_id);

  useEffect(() => {
    if (!ebankingCreationPending) {
      navigate(siteMap.homepage.path);
    }
  }, [ebankingCreationPending]);

  const onSubmit = values => {
    if (allowed) {
      dispatch(setLoader(false));
      setIsSlideModalOpen(true);
      setValues(values);
    } else {
      dispatch(setMessage(t("common.countryNotAllowed"), 'error'));
    }
  };

  const onLogout = () => dispatch(setLogout());

  const onSignUp = async values => {
    try {
      dispatch(setLoader(true));
      reset();
      const response = await dispatch(createInternetBankingUser(values));
      if(response) dispatch(setLoader(false));
      if (!response) {
        // Dispatch getPersonData. This will change the value of 'ebanking_user_creation_pending' to 'false' and redirect accordingly.
        dispatch(getPersonData(userIdBifrost, lastProductRequestId)).then(
          (res) => dispatch(setLoader(false)));
        await dispatch(setAvatarFromPersonData(response));
      }
    } catch (error) {
      console.error('onSignUp in useInternetBanking: ', error);
      if (!loading) dispatch(setLoader(false));
      dispatch(setMessage(t("eBanking.error"), 'error'));
    }
  };

  return {
    errors,
    register,
    handleSubmit,
    onSubmit,
    isSlideModalOpen,
    setIsSlideModalOpen,
    values,
    loading,
    selectedProduct,
    userId,
    onLogout,
    onSignUp
  };
};

/**
 * Hook to get the values to RecoverPasswordEmail
 * @returns
 */
export const useRecoverPasswordEmail = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loading } = useSelector(state => state.base);
  const { appOrigin, applications } = useSelector(state => state.user);
  const methods = useForm({
    resolver: yupResolver(schemaEmail),
    defaultValues: defaultValuesEmail
  });

  const { register, formState: { errors }, handleSubmit, reset } = methods

  useEffect(() => {
    if (!applications) dispatch(getUserApplications());
  }, [applications, dispatch]);

  const onRecover = async values => {
    try {
      dispatch(setLoader(true));
      reset();
      values['application_origin'] = appOrigin;
      await dispatch(recoverPasswordEmail(values));
      dispatch(setLoader(false));
    } catch (error) {
      console.error('onRecover in useRecoverPasswordEmail: ', error);
      if (!loading) dispatch(setLoader(false));
      dispatch(setMessage(t("message.changePassword"), 'error'));
    }
  };

  return { errors, handleSubmit, onRecover, register, methods };
};

/**
 * Hook to get the values to Change Password in recover password
 * @returns
 */
export const useRecoverPassword = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const appOriginParam = params?.hash.split('&').pop().replace('app_origin=', '');
  const { t } = useTranslation();
  const { loading } = useSelector(state => state.base);
  const { appOrigin, isPassword } = useSelector(state => state.user);
  const { register, formState: { errors }, handleSubmit, reset } = useForm({
    resolver: yupResolver(schemaRecoverPassword),
    defaultValues: defaultValuesRecoverPassword
  });

  useEffect(() => {
    if (appOrigin !== appOriginParam) dispatch(
      { type: USER_APP_ORIGIN, payload: appOriginParam });
  }, [appOriginParam, appOrigin]);

  useEffect(() => {
    if (isPassword) {
      navigate(siteMap.login.path2);
      dispatch(setPassword(false));
    }
  }, [isPassword]);

  const onRecover = async values => {
    try {
      dispatch(setLoader(true));
      reset();
      values['hash'] = params?.hash.split('&').shift();
      await dispatch(changePassword(values));
      dispatch(setLoader(false));
    } catch (error) {
      if (!loading) dispatch(setLoader(false));
      console.error('onRecover in useRecoverPassword: ', error);
      dispatch(setMessage(t("message.changePassword"), 'error'));
    }
  };

  return { errors, handleSubmit, onRecover, register };
};

/**
 * Hook to get the values to Homepage
 * @returns
 */
export const useHomepage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { appOrigin, code, level, username, applications } = useSelector(
    state => state.user);
  const { expiredFiles, rejectedFiles } = useSelector(state => state.base);
  const { dataProductRequest } = useSelector(state => state.user);
  const [isProcessingDocuments, setIsProcessingDocuments] = useState(false);
  const { data: personData } = useSelector(state => state?.personData)


  const routeProducts = () =>
    navigate(routes.productsServices('product_and_services'));

  const routeRequests = () =>
    navigate(routes.productRequests('product_requests'));

  const routeCreatedProducts = () =>
    navigate(routes.createdProducts('edition_requests') );

  const routeProfile = () => navigate(routes.profile('profile'));

  const routeExpired = () => navigate(routes.files(fileStatus.expired));

  const routeRejected = () => navigate(routes.files(fileStatus.rejected));

  // * options for OptionsMenu component
  const { t } = useTranslation()
  const options = [
    {
      onClick: routeProfile,
      text: t("profile.name"),
      icon: CiUser
    },
    {
      onClick: routeProducts,
      text: t("common.productsServices"),
      icon: CiShoppingCart
    },
    {
      onClick: routeRequests,
      text: t("common.productRequest"),
      icon: CiViewList
    }
  ]

  const onLogout = () => dispatch(setLogout());

  useEffect(() => {
    const productRequest = actualProductRequest();
    if (productRequest) {
      setIsProcessingDocuments(productRequest.processing_documents);
    }
  }, [dataProductRequest]);

  useEffect(() => {
    const getApplications = async() => {
      let productsAndServices = [];
      if (!applications) {
        productsAndServices = await dispatch(getUserApplications());
      } else {
        productsAndServices = applications;
      }
      let appOriginData = typeof appOrigin === 'string' ? appOrigin : appOrigin?.appOriginData
      let product = productsAndServices.filter(
        product => product?.application?.unique_name === appOriginData);
      if (product.length === 0 ) product = [{image_main: logoBgAppOrigin.default }];
      const [productRequest] = product;
      dispatch({ type: USER_INFO_PRODUCT_REQUEST, payload: productRequest });
    };

    getApplications();
  }, [dispatch, appOrigin]);

  return {
    code,
    expiredFiles,
    level,
    onLogout,
    rejectedFiles,
    routeExpired,
    routeProducts,
    routeProfile,
    routeRejected,
    username,
    routeRequests,
    routeCreatedProducts,
    isProcessingDocuments,
    options,
    dataProduct: dataProductRequest?.[0],
    personData
  };
};

/**
 * Custom hook for checking products' existance
 * It also updatesPersonData after coming out of a tier.
 * Custom Hook
 * @returns
 */
export const useExistantProducts = () => {
  const { actualLanguage } = useSelector(state => state.base);
  const {
    userIdBifrost: bifrostUserId,
    lastProductRequestId
  } = useSelector(state => state?.user)
  const [clientProducts, setClientProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch()

  const updateData = async () => {
    dispatch(setLoader(true))
    await dispatch(updatePersonData(bifrostUserId, lastProductRequestId)) 
    dispatch(setLoader(false))
  }

  const getClientProducts = async () => {
    setLoading(true);
    const result = await sendGetEndpoint(endpoints.clientProducts(bifrostUserId));
    setClientProducts(result?.success);
    setLoading(false);
  };

  useEffect(() => {
    updateData()
    getClientProducts();
  }, []);

  return { clientProducts, actualLanguage, loading };
};

/**
 * Hook to get the values to Profile
 * @returns
 */
export const useInformation = () => {
  const { t } = useTranslation();
  const { data: personData } = useSelector(state => state.personData);
  const { fields } = useSelector(state => state?.tier?.tiers?.basic)
  const email = [];
  const general = [];
  const phone = [];

  // Email
  if (personData?.personal_email) {
    email.push({
      text: personData.personal_email,
      name: t("common.email"),
      key: profile.personalEmail,
      lg: 6
    });
  }
  if (personData?.business_email) {
    email.push({
      text: personData.business_email,
      name: t("profile.businessEmail"),
      key: profile.businessEmail,
      lg: 6
    });
  }

  // General
  if (personData?.first_name && personData?.last_name) {
    general.push({
      text: `${personData?.first_name} ${personData?.second_name ?? ''} ${
        personData?.last_name} ${personData?.second_last_name ?? ''}`,
      name: t("profile.fullName"),
      key: profile.fullName
    });
  }

  // Phone
  if (personData.user_contact) {
    phone.push({
      name: t("profile.cellPhone"),
      key: profile.cellPhone,
      phones: personData.user_contact,
      fields: fields.filter(field => field.unique_name.includes('block')).sort((a, b) => {
        let groupA = a.unique_name.split('__')[0]
        let groupB = b.unique_name.split('__')[0]
        return groupA.localeCompare(groupB)
      })
    });
  }

  return { email, general, phone };
};

/* Hook to get email value */
export const useEmailUser = () => {
  const { t } = useTranslation();
  const { data: personData } = useSelector(state => state.personData);
  const email = [];

  // Email
  if (personData?.personal_email) {
    email.push({
      text: personData.personal_email,
      name: t("common.email"),
      key: profile.personalEmail,
      lg: 6
    });
  }
  if (personData?.business_email) {
    email.push({
      text: personData.business_email,
      name: t("profile.businessEmail"),
      key: profile.businessEmail,
      lg: 6
    });
  }
  return {
    email
  }
}

/**
 * Hook to get the values to ClientEntity
 * @returns
 */
export const useClientEntity = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [productsList, setProductsList] = useState([]);
  const [oldMask, setOldMask] = useState(null);
  const [country, setCountry] = useState(null);
  const { loading } = useSelector(state => state.base);
  const { applications, dataSummary } = useSelector(state => state.user);
  const methods = useForm({
    resolver: yupResolver(schemaClientEntity),
    defaultValues: defaultValuesClientEntity
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    setError
  } = methods

  useEffect(() => {
    if (!applications) {
      dispatch(getUserApplications());
    } else {
      const products = applications.filter(
        product => product?.application?.active && product?.application?.create_account).map(
          (item) => {
            return item.application;
      });
      setProductsList(products);
    };
  }, [applications, dispatch]);

  useEffect(() => {
    if (!dataSummary) {
      dispatch(getDataSummary());
    } else {
      const documentType = dataSummary.document_type.filter(item => !item.company).sort(
        (a,b) => a.description.localeCompare(b.description));
      const country = dataSummary.country.sort((a,b) => a.name.localeCompare(b.name));
      dataSummary.document_type = documentType;
      dataSummary.country = country;
      setCountry(dataSummary);
    };
  }, [dataSummary, dispatch]);

  const setValuesCountry = value => {
    setValue("document_country_id", value);
    const message = (!isEmpty(value))
      ? ''
      : t("common.fieldRequired");
    setError('document_country_id', { message: message });
  };

  const setValuesDocument = value => {
    const [document_additional_info] = country.document_additional_info.filter(
      mask => mask.document_type === parseInt(value));
    const documentField = fieldUniqueName('document_number');
    if (document_additional_info) {
      setMask(documentField, paramsAdd, JSON.stringify(document_additional_info?.mask));
      setOldMask(document_additional_info?.mask);
    } else {
      setMask(documentField, 'remove', JSON.stringify(oldMask));
    }
    const message = (!isEmpty(value))
      ? ''
      : t("common.fieldRequired");
    setError('document_type_id', { message: message });
  };

  const setValuePhoneCountry = value => {
    setValue("cell_phone_country_id", value);
    const message = (!isEmpty(value))
      ? ''
      : t("common.fieldRequired");
    setError('cell_phone_country_id', { message: message });
  };

  const onRegister = async values => {
    try {
      dispatch(setLoader(true));
      values['cell_phone'] = (values['cell_phone'].substring(values['cell_phone'].indexOf(' ') + 1))
        .split(' ').join('');
      values['document_number'] = values['document_number'].split(' ').join('').split('-').join('');
      await dispatch(registerClientEntity(values));
      reset();
      dispatch(setLoader(false));
    } catch (error) {
      console.error('onRegister: error in client entity', error);
      if (!loading) dispatch(setLoader(false));
      dispatch(setMessage(t("message.signUpUser"), 'error'));
    }
  };

  return {
    country,
    errors,
    handleSubmit,
    onRegister,
    register,
    setValuesCountry,
    setValuesDocument,
    setValuePhoneCountry,
    methods
  };
};